import { motion } from "framer-motion";
import { ShieldCheck, Clock, Sparkles, IndianRupee, MapPin, MousePointerClick, Navigation, UserCheck } from "lucide-react";

const CHAPTERS = [
  { num: "01", title: "Round-the-clock service", desc: "24/7 dispatch — every hour, every festival, every emergency. We answer.", icon: Clock },
  { num: "02", title: "Professional drivers", desc: "Verified, courteous, uniformed and route-savvy across Rajasthan.", icon: UserCheck },
  { num: "03", title: "Sanitized vehicles", desc: "Deep-cleaned interiors, sanitized handrails, fresh linens on long trips.", icon: Sparkles },
  { num: "04", title: "Transparent pricing", desc: "No hidden charges. Fixed rates for airport, station and outstation runs.", icon: IndianRupee },
  { num: "05", title: "Safe journey guarantee", desc: "GPS-tracked, insured & audited fleet for every kilometre you travel.", icon: ShieldCheck },
  { num: "06", title: "Instant booking", desc: "Book on WhatsApp in 30 seconds. Get a driver within minutes.", icon: MousePointerClick },
  { num: "07", title: "GPS-enabled cars", desc: "Live tracking share links so family always knows where you are.", icon: Navigation },
  { num: "08", title: "On-time pickup", desc: "97% of our pickups are early. Because we know a flight cannot wait.", icon: MapPin },
];

export default function WhyChooseUs() {
  return (
    <section id="about" className="relative bg-brand-cream text-brand-ink py-24 md:py-32" data-testid="why-choose-us">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-4 md:sticky md:top-28">
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">Chapter I · The Manifesto</div>
            <h2 className="mt-4 font-display font-black text-5xl md:text-6xl leading-[1.02]">
              Why <span className="italic text-brand-orange font-medium">travellers</span> choose Mamta Tours & Travels.
            </h2>
            <p className="mt-6 text-brand-ink/70 max-w-md">
              A decade on Rajasthan's roads has taught us one thing — travel is trust, delivered kilometre by kilometre. These are the eight promises we keep.
            </p>
          </div>

          <div className="md:col-span-8 grid sm:grid-cols-2 gap-4 md:gap-5">
            {CHAPTERS.map((c, i) => (
              <motion.article
                key={c.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                className="group relative rounded-3xl bg-white p-6 border border-brand-ink/5 hover:border-brand-blue/30 transition-colors shadow-[0_1px_0_rgba(0,0,0,0.03)] hover:shadow-[0_20px_60px_-30px_rgba(11,77,184,0.35)]"
                data-testid={`chapter-${c.num}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-display text-4xl font-black text-brand-blue/15 group-hover:text-brand-orange/70 transition-colors">{c.num}</span>
                  <div className="w-11 h-11 rounded-2xl bg-brand-blue/8 text-brand-blue flex items-center justify-center group-hover:bg-brand-orange group-hover:text-white transition-colors">
                    <c.icon className="w-5 h-5" />
                  </div>
                </div>
                <h3 className="mt-5 font-display text-xl font-bold">{c.title}</h3>
                <p className="mt-2 text-sm text-brand-ink/65 leading-relaxed">{c.desc}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
